import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './ExternalUrlEditor.html?raw';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../../moreCore/UI/Container/uiFrame';
import { ExternalUrl } from "../ScreenplayTypes";
import lang from 'dojo/_base/lang';
import { TabContainer } from "../../../moreCore/_loadingHelpers/Tabbing";
import NumberInput from '../../../moreCore/UI/NumberInput/NumberInput';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import _GlobalSelectEntityDialogsMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import { uiFrameTypes } from "../../../moreCore/UI/Container/uiFrameTypes";

class ExternalUrlEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private inputInterval: MdsLax<InstanceType<typeof NumberInput>>;

    isNew: boolean = false;
    reloadAfterSave: boolean = true;

    setReloadAfterSave(reload: boolean) {
        this.reloadAfterSave = reload;
    }

    setIsNew(isNew: boolean) {
        this.isNew = isNew;
        this.enableButton("save");
    }

    setup(tabContainer: MdsLax<InstanceType<typeof TabContainer>>, entity?: ExternalUrl,mode: uiFrameTypes.DisplayMode = "tab" , node?: HTMLElement): void {
        let heading;

        if (entity) {
            this.internalName = "externalUrlEditor_" + entity.ID;
            if (entity.is_new) {
                this.setData(entity);
                heading = this.translate("Screenplay_Heading_NewExternalUrl");
            } else {
                heading = `${this.translate("Screenplay_Heading_ExternalUrl")}: ${entity.description}`;
                entity.is_new = false;
                this.setStartupState("ready");
                this._loadEntity(entity.ID);
            }
        } else {
            this.internalName = "externalUrlEditor_New";
            let newEntity: ExternalUrl = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                description: "",
                content_change_interval: null,
                web_url: "",
                is_new: true
            }
            entity = newEntity;
            this.setData(entity);
            heading = this.translate("Screenplay_Heading_NewExternalUrl")
        }


        if (tabContainer && mode == "tab") {
            this.show(mode, heading, tabContainer.name);
        } else if (node && mode == "node") {
            this.show(mode, heading, null, node);
        } else {
            this.show("dlg", heading, tabContainer.name);
        }

        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), this.isNew, "primary");
    }

    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__ExternalUrl_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: ExternalUrl): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickSave(evt: MouseEvent, isBeforeClose: boolean = false): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);

        this.emit("clickedSave");

        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__ExternalUrl_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse, isBeforeClose),
            lang.hitch(this, this._onSaveError));
    }

    private _onSaveResponse(isBeforeClose: boolean = false, response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        if (this.reloadAfterSave) this._loadEntity(response.entityId);
    }

    private _onSaveError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickRollback(e): void {
        this.rollback();
    }

    private _onClickReload(e): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    private _onClickDelete(e): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__ExternalUrl_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        if (!this.isNew) this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;


        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(
                this.translate("Screenplay_ExternalUrl_AskSavePendingChanges"),
                this.translate("General_PendingChanges") + " - " + this.title,
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.emit("notSaved");
            this.close();
        } else if (result == 0) { // cancel
            this.emit("canceled");
        }
        else {
            this._onClickSave(null, true);
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        let isNew = this.getAttributeValue("is_new");
        if (!isNew) {
            this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("externalUrlEditor_" + this.getAttributeValue("ID"));
            this.setTitles(`${this.translate("Screenplay_Heading_ExternalUrl")}: ${this.getAttributeValue("description")}`);
        } else {

        }
        this._deactivateDataButtons(true);
    }

    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {

        }
    }

    protected onDirtyChange(newState: boolean) {   
        this._deactivateDataButtons(!newState);
        this.setDirtyMarker(newState);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.inputInterval.setUnit("s");
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(ExternalUrlEditor);